import React from "react";
import dashboardStyle from "../styles/Dashboard.module.css";
import styles from "../styles/SizeFlow.module.css";

const groupSizesByItem = (sizes = []) =>
  sizes.reduce((acc, talle, idx) => {
    const itemKey = talle.OrderItemCategory?.name ?? talle.item ?? `Categoría ${talle.order_item_category_id}`;
    if (!acc[itemKey]) acc[itemKey] = [];
    acc[itemKey].push({ ...talle, index: idx });
    return acc;
  }, {});

export const SizeFlowShell = ({ title, titleAccent, subtitle, eyebrow, onClose, children }) => (
  <div className={dashboardStyle.modalBackground}>
    <div className={styles.shell}>
      <button className={dashboardStyle.closer} onClick={onClose} aria-label="Cerrar formulario de talles">
        <i className="bi bi-x-lg" />
      </button>
      <div className={styles.content}>
        <div className={styles.header}>
          <span className={styles.eyebrow}>{eyebrow}</span>
          <h2 className={styles.title}>
            {title} {titleAccent ? <span className={styles.titleAccent}>{titleAccent}</span> : null}
          </h2>
          {subtitle ? <p className={styles.subtitle}>{subtitle}</p> : null}
        </div>
        <div className={styles.body}>{children}</div>
      </div>
    </div>
  </div>
);

export const SizeProductSummary = ({ title, alias, metrics = [] }) => (
  <div className={styles.summaryCard}>
    <div className={styles.summaryTop}>
      <span className={styles.summaryEyebrow}>Prenda seleccionada</span>
      <p className={styles.summaryTitle}>{title}</p>
      {alias ? <p className={styles.summaryAlias}>{alias}</p> : null}
    </div>
    <div className={styles.metricRow}>
      {metrics.map((metric) => (
        <div key={metric.label} className={styles.metricPill}>
          <span className={styles.metricLabel}>{metric.label}</span>
          <span className={styles.metricValue}>{metric.value}</span>
        </div>
      ))}
    </div>
  </div>
);

export const SizeAlertBanner = ({ icon, title, text }) => (
  <div className={styles.alertBanner}>
    <div className={styles.alertIcon}>
      <img src={icon} alt="Aviso" />
    </div>
    <div className={styles.alertCopy}>
      <p className={styles.alertTitle}>{title}</p>
      <p className={styles.helperText}>{text}</p>
    </div>
  </div>
);

export const SizeField = ({ label, children, hint }) => (
  <div className={styles.field}>
    <label className={styles.fieldLabel}>{label}</label>
    {children}
    {hint ? <p className={styles.inputHint}>{hint}</p> : null}
  </div>
);

export const EmojiTipsCard = ({ emojiOpen, onToggle, image }) => (
  <div className={styles.helperCard}>
    <div className={styles.catalogHeader}>
      <div>
        <span className={styles.cardEyebrow}>Apodos y emojis</span>
        <p className={styles.cardTitle}>Tené en cuenta estos tips</p>
      </div>
      <button type="button" className={styles.catalogToggle} onClick={onToggle}>
        Catálogo de emojis <i className={`bi ${emojiOpen ? "bi-chevron-up" : "bi-chevron-down"}`} />
      </button>
    </div>

    <div className={styles.emojiTips}>
      <div className={styles.tipRow}>
        <span className={styles.tipIcon}><i className="bi bi-x-lg" /></span>
        <p className={styles.tipText}>Si no querés que lleve apodo, podés dejar ese campo vacío.</p>
      </div>
      <div className={styles.tipRow}>
        <span className={styles.tipIcon}><i className="bi bi-stars" /></span>
        <p className={styles.tipText}>El emoji solo puede ir adelante y/o atrás del apodo, nunca en el medio.</p>
      </div>
      <div className={styles.tipRow}>
        <span className={styles.tipIcon}><i className="bi bi-chat-heart" /></span>
        <p className={styles.tipText}>Algunos emojis pueden tener costo extra, así que siempre conviene validarlo con tu asesor/a.</p>
      </div>
    </div>

    {emojiOpen ? (
      <div className={styles.catalogCard}>
        <p className={styles.catalogHint}>Elegí del catálogo visual para mantener consistencia en producción.</p>
        <img src={image} alt="Catálogo de emojis" className={styles.catalogImage} />
      </div>
    ) : null}
  </div>
);

export const SizeSections = ({
  title,
  hint,
  prenda,
  tallesPorMolderia,
  visibilidadMolderia,
  tallesSeleccionados,
  onToggleSeccion,
  onTalleChange,
  onOpenGuide,
}) => (
  <div className={styles.fieldCard}>
    <div className={styles.sectionHeaderMain}>
      <span className={styles.sectionEyebrow}>Selección de talles</span>
      <p className={styles.sectionTitle}>{title}</p>
      {hint ? <p className={styles.sectionHint}>{hint.replace("{prenda}", prenda)}</p> : null}
    </div>

    {/* La moldería ya viene definida para la prenda (se elige desde el backoffice),
        así que NO se muestra el selector de moldería: los talles se listan directo. */}
    <div className={styles.sectionList}>
      {Object.keys(tallesPorMolderia).map((molderia) => {
        const grouped = groupSizesByItem(tallesPorMolderia[molderia]);

        return (
          <div key={molderia} className={styles.sectionCard}>
            <div className={styles.sectionBody}>
              {Object.entries(grouped).map(([itemName, tallesGrupo]) => (
                <div key={itemName} className={styles.itemGroup}>
                  <p className={styles.itemGroupTitle}>{itemName}</p>
                  <div className={styles.sizeGrid}>
                    {tallesGrupo.map(({ code, index }) => {
                      const selected = Boolean(tallesSeleccionados[molderia]?.[index]);
                      return (
                        <label
                          key={`${molderia}-${code}-${index}`}
                          className={`${styles.sizeChip} ${selected ? styles.sizeChipSelected : ""}`}
                        >
                          <input
                            type="checkbox"
                            className={styles.hiddenCheckbox}
                            checked={selected}
                            onChange={() => onTalleChange(molderia, index)}
                          />
                          {code}
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}

              <div className={styles.guideRow}>
                <button type="button" className={styles.guideButton} onClick={() => onOpenGuide(molderia)}>
                  <i className="bi bi-rulers" /> Ver guía de talles
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

export const CurrentSizeCard = ({ currentSize, onEdit, editMode, onCancelEdit }) => (
  <div className={styles.infoCard}>
    <span className={styles.cardEyebrow}>Estado actual</span>
    <p className={styles.cardTitle}>Talle elegido</p>
    <div className={styles.currentSizeCard}>
      <div className={styles.currentSizeRow}>
        <span className={styles.currentSizeValue}>{currentSize || "Sin talle"}</span>
        {!editMode ? (
          <button type="button" className={styles.inlineAction} onClick={onEdit}>
            Editar talle
          </button>
        ) : null}
      </div>
      {editMode ? (
        <button type="button" className={styles.ghostAction} onClick={onCancelEdit}>
          Cancelar edición
        </button>
      ) : null}
    </div>
  </div>
);

export const SizeFooter = ({ onBack, onSubmit, submitLabel = "Enviar", disabled = false }) => (
  <div className={styles.footer}>
    <button type="button" className={styles.secondaryBtn} onClick={onBack}>
      Atrás
    </button>
    <button type="button" className={styles.primaryBtn} onClick={onSubmit} disabled={disabled}>
      {submitLabel}
    </button>
  </div>
);

export const SizeConfirmationModal = ({
  open,
  title,
  subtitle,
  alumno,
  apodo,
  personalizado,
  tallesPorMolderia,
  onClose,
  onConfirm,
  confirmLabel = "Confirmar",
  confirmLoading = false,
}) => {
  if (!open) return null;

  const molderias = Object.entries(tallesPorMolderia || {});

  return (
    <div className={dashboardStyle.modalBackground}>
      <div className={styles.confirmShell}>
        <button className={dashboardStyle.closer} onClick={onClose} aria-label="Cerrar confirmación de talles">
          <i className="bi bi-x-lg" />
        </button>
        <div className={styles.confirmContent}>
          <div className={styles.confirmHeader}>
            <span className={styles.confirmEyebrow}>Confirmación final</span>
            <p className={styles.confirmTitle}>{title}</p>
            {subtitle ? <p className={styles.confirmText}>{subtitle}</p> : null}
          </div>

          <div className={styles.confirmCard}>
            <div className={styles.confirmRows}>
              <div className={styles.confirmRow}>
                <span className={styles.confirmLabel}>Alumno</span>
                <span className={styles.confirmValue}>{alumno || "-"}</span>
              </div>
              <div className={styles.confirmRow}>
                <span className={styles.confirmLabel}>Apodo</span>
                <span className={`${styles.confirmValue} ${!apodo ? styles.confirmEmpty : ""}`}>{apodo || "Sin apodo"}</span>
              </div>
              <div className={styles.confirmRow}>
                <span className={styles.confirmLabel}>Personalizado</span>
                <span className={`${styles.confirmValue} ${!personalizado ? styles.confirmEmpty : ""}`}>{personalizado || "Sin aclaraciones"}</span>
              </div>
              <div className={styles.confirmRow}>
                <span className={styles.confirmLabel}>Talles elegidos</span>
                <div className={styles.confirmRows}>
                  {molderias.length > 0 ? molderias.map(([molderia, talles]) => (
                    <div key={molderia} className={styles.confirmRow}>
                      <span className={styles.confirmValue}>{molderia}</span>
                      <div className={styles.confirmPills}>
                        {talles.map((talle) => (
                          <span key={`${molderia}-${talle}`} className={styles.confirmPill}>{talle}</span>
                        ))}
                      </div>
                    </div>
                  )) : <span className={`${styles.confirmValue} ${styles.confirmEmpty}`}>Sin talle seleccionado</span>}
                </div>
              </div>
            </div>
          </div>

          <div className={styles.confirmFooter}>
            <button type="button" className={styles.cancelBtn} onClick={onClose}>
              Cancelar
            </button>
            <button type="button" className={styles.confirmBtn} onClick={onConfirm} disabled={confirmLoading}>
              {confirmLoading ? `${confirmLabel}...` : confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
